import { makeStyles } from 'tss-react/mui';
import { GLOBAL_SCROLLBAR_HEIGHT } from '../../../../../../../themes/v2/V2GlobalStyles';

const CARD_GAP = 0.5;
const EDGE_FADE_WIDTH = 5;
const SCROLLBAR_TRACK_INSET = 0.75;

interface ProductCardsCarouselStylesParams {
  navTop: number;
}

export const useProductCardsCarouselStyles = makeStyles<
  ProductCardsCarouselStylesParams,
  'track'
>()((theme, { navTop }, classes) => {
  const sentinelOffset = `calc(-1px - ${theme.spacing(CARD_GAP)})`;
  const trackBottomSpacing = theme.spacing(1);
  const fadeBottom = `calc(${trackBottomSpacing} + ${GLOBAL_SCROLLBAR_HEIGHT}px)`;
  const fadeWidth = theme.spacing(EDGE_FADE_WIDTH);
  const edgeTransition = theme.transitions.create(['opacity', 'visibility'], {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  });

  return {
    root: {
      position: 'relative',
      width: '100%',
      minWidth: 0,
      [`&:hover .${classes.track}::-webkit-scrollbar-thumb`]: {
        background: theme.palette.action.active,
      },
      '@supports not selector(::-webkit-scrollbar)': {
        [`&:hover .${classes.track}`]: {
          scrollbarColor: `${theme.palette.action.active} transparent`,
        },
      },
    },
    track: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(CARD_GAP),
      overflowX: 'auto',
      overscrollBehaviorX: 'contain',
      scrollSnapType: 'x proximity',
      scrollPaddingInline: fadeWidth,
      paddingBottom: trackBottomSpacing,
      scrollbarColor: 'auto',
      '@supports not selector(::-webkit-scrollbar)': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
      },
      '&::-webkit-scrollbar-track': {
        marginInline: theme.spacing(SCROLLBAR_TRACK_INSET),
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'transparent',
      },
    },
    startSentinel: {
      flex: '0 0 1px',
      alignSelf: 'stretch',
      marginInlineEnd: sentinelOffset,
    },
    endSentinel: {
      flex: '0 0 1px',
      alignSelf: 'stretch',
      marginInlineStart: sentinelOffset,
    },
    edge: {
      transition: edgeTransition,
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    edgeHidden: {
      opacity: 0,
      visibility: 'hidden',
    },
    fade: {
      position: 'absolute',
      top: 0,
      bottom: fadeBottom,
      width: fadeWidth,
      pointerEvents: 'none',
    },
    fadeStart: {
      left: 0,
      background: `linear-gradient(to left, transparent, ${theme.palette.custom.layout.workspace})`,
    },
    fadeEnd: {
      right: 0,
      background: `linear-gradient(to right, transparent, ${theme.palette.custom.layout.workspace})`,
    },
    nav: {
      top: navTop,
    },
  };
});
